import moment from 'moment'
import chunk from 'lodash/chunk'
import { call, put, select, take, race, delay, fork, cancel } from 'redux-saga/effects'
import { ArtistSource, MomentFormat } from 'enums'
import {
  getArtistAlbums,
  getFullAlbums,
  getUser,
  getUserFollowedArtistsPage,
  getUserSavedAlbumsPage,
  getUserSavedTracksPage,
} from 'api'
import { getAuthData, getSyncScopes } from 'auth'
import {
  buildAlbumsMap,
  buildArtist,
  deleteAlbums,
  deleteArtists,
  deleteLabels,
  mergeAlbumsRaw,
} from 'helpers'
import { albumsNew, albumsHistory } from 'albums'
import {
  getSettings,
  getReleasesMaxDate,
  getSettingsBlockedArtists,
  getSettingsBlockedAlbums,
  getSettingsBlockedLabels,
} from 'state/selectors'
import {
  setFilters,
  setSyncingProgress,
  showErrorMessage,
  syncAnimationFinished,
  syncError,
  syncFinished,
  syncStart,
} from 'state/actions'
import { authorize } from './auth'
import { withTitle } from './helpers'
import { getAllCursorPaged, getAllPaged, putRequestMessage, setupWorkers } from './request'

const { ISO_DATE } = MomentFormat
const { FOLLOWED, SAVED_ALBUMS, SAVED_TRACKS } = ArtistSource

/**
 * How much percentage of overall progress is assigned to base sync when extra data fetch is enabled
 */
const BASE_SYNC_RATIO = 0.75

/**
 * Synchronization saga
 *
 * @param {SyncAction} action
 */
export function* syncSaga(action) {
  try {
    /** @type {ReturnType<typeof getSettings>} */
    const { artistSources } = yield select(getSettings)
    /** @type {ReturnType<typeof getSyncScopes>} */
    const scopes = yield call(getSyncScopes, artistSources)

    /** @type {ReturnType<typeof withTitle>} */
    const titled = yield call(withTitle, 'Loading...', syncMainSaga, action)
    /** @type {ReturnType<typeof authorize>} */
    const authorized = yield call(authorize, action, scopes, titled)

    yield call(authorized)
  } catch (error) {
    yield put(showErrorMessage(error.message ?? error.toString()))
    yield put(syncError())
  }
}

/**
 * Main synchronization saga
 *
 * @param {SyncAction} action
 */
function* syncMainSaga(action) {
  yield put(syncStart())

  /** @type {ReturnType<typeof getAuthData>} */
  const { token } = yield call(getAuthData)
  /** @type {ReturnType<typeof getSettings>} */
  const { days, fullAlbumData, trackHistory } = yield select(getSettings)
  /** @type {ReturnType<typeof getSettingsBlockedLabels>} */
  const blockedLabels = yield select(getSettingsBlockedLabels)
  /** @type {ReturnType<typeof getSettingsBlockedArtists>} */
  const blockedArtists = yield select(getSettingsBlockedArtists)
  /** @type {ReturnType<typeof getSettingsBlockedAlbums>} */
  const blockedAlbums = yield select(getSettingsBlockedAlbums)
  /** @type {ReturnType<typeof getReleasesMaxDate>} */
  const previousSyncMaxDate = yield select(getReleasesMaxDate)
  const minDate = days >= 0 ? moment().subtract(days, 'day').format(ISO_DATE) : undefined

  /** @type {RequestWorkers} */
  const { workers, requestChannel, responseChannel, workersFork } = yield call(setupWorkers)
  yield fork(workersFork)

  /** @type {Await<ReturnType<typeof getUser>>} */
  const user = yield call(getUser, token)
  /** @type {Artist[]} */
  const artists = yield call(getArtists, requestChannel, responseChannel, workers.length)

  /** @type {AlbumRaw[]} */
  const albumsRaw = yield call(syncBaseData, artists, requestChannel, responseChannel)
  /** @type {Await<ReturnType<typeof mergeAlbumsRaw>>} */
  const mergedAlbums = yield call(mergeAlbumsRaw, albumsRaw, minDate)
  /** @type {Await<ReturnType<typeof buildAlbumsMap>>} */
  const albums = yield call(buildAlbumsMap, mergedAlbums, artists)
  yield call(deleteArtists, albums, blockedArtists)
  yield call(deleteAlbums, albums, blockedAlbums)

  if (fullAlbumData) {
    yield call(syncExtraData, albums, requestChannel, responseChannel)
    yield call(deleteLabels, albums, blockedLabels)
  }

  if (trackHistory) {
    yield call(updateHistory, albums)
  }

  yield cancel(workers)
  yield put(setSyncingProgress(100))
  yield race([take(syncAnimationFinished.type), delay(1000)])
  yield put(syncFinished({ albums, user, previousSyncMaxDate, auto: action.payload?.auto }))
}

/**
 * Get artists based on selected sources
 *
 * @param {RequestChannel} requestChannel
 * @param {ResponseChannel} responseChannel
 * @param {number} workersCount
 */
function* getArtists(requestChannel, responseChannel, workersCount) {
  /** @type {ReturnType<typeof getSettings>} */
  const { artistSources } = yield select(getSettings)
  /** @type {ReturnType<typeof getSettingsBlockedArtists>} */
  const blockedArtists = yield select(getSettingsBlockedArtists)
  /** @type {Artist[]} */
  const allArtists = []
  /** @type {Record<string, Artist>} */
  const artists = {}

  if (artistSources.includes(FOLLOWED)) {
    /** @type {Artist[]} */
    const artists = yield call(getUserFollowedArtists, requestChannel, responseChannel)
    for (const artist of artists) allArtists.push(artist)
  }

  if (artistSources.includes(SAVED_TRACKS)) {
    /** @type {Artist[]} */
    const artists = yield call(
      getUserSavedTracksArtists,
      requestChannel,
      responseChannel,
      workersCount
    )
    for (const artist of artists) allArtists.push(artist)
  }

  if (artistSources.includes(SAVED_ALBUMS)) {
    /** @type {Artist[]} */
    const artists = yield call(
      getUserSavedAlbumsArtists,
      requestChannel,
      responseChannel,
      workersCount
    )
    for (const artist of artists) allArtists.push(artist)
  }

  for (const artist of allArtists) {
    if (artist.id in artists) continue
    if (blockedArtists.includes(artist.id)) continue
    artists[artist.id] = artist
  }

  return Object.values(artists)
}

/**
 * Fetch base album data
 *
 * @param {Artist[]} artists
 * @param {RequestChannel} requestChannel
 * @param {ResponseChannel} responseChannel
 */
function* syncBaseData(artists, requestChannel, responseChannel) {
  /** @type {AlbumRaw[]} */
  const albumsRaw = []
  /** @type {ReturnType<typeof getAuthData>} */
  const { token } = yield call(getAuthData)
  /** @type {ReturnType<typeof getSettings>} */
  const { groups, fullAlbumData } = yield select(getSettings)

  for (const artist of artists)
    yield putRequestMessage(requestChannel, [getArtistAlbums, token, artist.id, groups])

  for (let fetched = 0; fetched < artists.length; fetched += 1) {
    /** @type {ResponseChannelMessage<Await<ReturnType<typeof getArtistAlbums>>>} */
    const response = yield take(responseChannel)

    let newProgress = ((fetched + 1) / artists.length) * 100
    if (fullAlbumData) newProgress *= BASE_SYNC_RATIO
    yield put(setSyncingProgress(newProgress))

    if (response.error) continue
    for (const album of response.result) albumsRaw.push(album)
  }

  return albumsRaw
}

/**
 * Fetch extra album data
 *
 * @param {AlbumsMap} albums
 * @param {RequestChannel} requestChannel
 * @param {ResponseChannel} responseChannel
 */
function* syncExtraData(albums, requestChannel, responseChannel) {
  /** @type {ReturnType<typeof getAuthData>} */
  const { token } = yield call(getAuthData)
  const albumIdsChunks = chunk(Object.keys(albums), 20)

  for (const albumIdsChunk of albumIdsChunks)
    yield putRequestMessage(requestChannel, [getFullAlbums, token, albumIdsChunk])

  for (let fetched = 0; fetched < albumIdsChunks.length; fetched += 1) {
    /** @type {ResponseChannelMessage<Await<ReturnType<typeof getFullAlbums>>>} */
    const response = yield take(responseChannel)

    let newProgress = ((fetched + 1) / albumIdsChunks.length) * 100
    newProgress *= 1 - BASE_SYNC_RATIO
    newProgress += BASE_SYNC_RATIO * 100
    yield put(setSyncingProgress(newProgress))

    if (response.error) continue

    for (const albumFull of response.result) {
      const { id, label, popularity } = albumFull
      Object.assign(albums[id], { label, popularity })
    }
  }
}

/**
 * @param {RequestChannel} requestChannel
 * @param {ResponseChannel} responseChannel
 */
function* getUserFollowedArtists(requestChannel, responseChannel) {
  /** @type {SpotifyArtist[]} */
  const artists = yield call(
    getAllCursorPaged,
    getUserFollowedArtistsPage,
    requestChannel,
    responseChannel
  )

  return artists.map(buildArtist)
}

/**
 * @param {RequestChannel} requestChannel
 * @param {ResponseChannel} responseChannel
 * @param {number} workersCount
 */
function* getUserSavedTracksArtists(requestChannel, responseChannel, workersCount) {
  /** @type {ReturnType<typeof getSettings>} */
  const { minimumSavedTracks } = yield select(getSettings)
  /** @type {Record<string, { count: number; artist: Artist}>} */
  const artists = {}

  /** @type {SpotifySavedTrack[]} */
  const tracks = yield call(
    getAllPaged,
    getUserSavedTracksPage,
    requestChannel,
    responseChannel,
    workersCount
  )

  for (const item of tracks) {
    for (const artist of item.track.artists) {
      if (artist.id in artists) {
        artists[artist.id].count++
        continue
      }

      artists[artist.id] = { count: 1, artist }
    }
  }

  return Object.values(artists)
    .filter(({ count }) => count >= minimumSavedTracks)
    .map(({ artist }) => buildArtist(artist))
}

/**
 * @param {RequestChannel} requestChannel
 * @param {ResponseChannel} responseChannel
 * @param {number} workersCount
 */
function* getUserSavedAlbumsArtists(requestChannel, responseChannel, workersCount) {
  /** @type {Record<string, Artist>} */
  const artists = {}

  /** @type {SpotifySavedAlbum[]} */
  const albums = yield call(
    getAllPaged,
    getUserSavedAlbumsPage,
    requestChannel,
    responseChannel,
    workersCount
  )

  for (const item of albums) {
    for (const artist of item.album.artists) {
      if (artist.id in artists) continue
      artists[artist.id] = artist
    }
  }

  return Object.values(artists).map(buildArtist)
}

/** @param {AlbumsMap} albums */
function* updateHistory(albums) {
  albumsHistory.append(albumsNew)
  yield call(albumsNew.clear)

  for (const id of Object.keys(albums)) {
    if (albumsHistory.has(id)) continue
    albumsNew.add(id)
  }

  yield call(albumsNew.persist)
  yield call(albumsHistory.persist)

  if (albumsNew.size > 0 && albumsHistory.size > 0) {
    yield put(setFilters({ newOnly: true }))
  }
}
